import { useState, type CSSProperties } from 'react';
import type {
  ControlHistory,
  ControlState,
  FailingRun,
  Framework,
  ProgressReport,
  Remediation,
  Verdict,
} from '../api.ts';
import { asDate, plural } from './format.ts';
// The tally tiles, the caveat list and the em-dash placeholder are the ones the
// drift view already draws, and they must look identical: an MSP reads the two
// screens minutes apart. Imported rather than assumed present.
import '../results.css';
import '../progress.css';

/**
 * How a client has moved across its recent assessments.
 *
 * Its own tab rather than an addition to Assessments. That tab is the run log —
 * when each assessment started, whether it finished, where its report is — and
 * it answers an operational question. This answers a client-facing one: which
 * controls have been open for how long, what has been put right, and where our
 * own rule changes mean the two sides of a history cannot be compared. Folding
 * the second into the first would bury it under a table of timestamps, and the
 * Overview tab is deliberately about the latest assessment alone.
 *
 * Every judgement here was made by the server. This file formats dates, picks
 * words, and renders `qualification` wherever it is present — which is the one
 * rule it must not break, because that sentence is what stops a duration being
 * read as a measurement when it is a floor.
 *
 * No score, percentage, grade or ratio, and nothing here divides one count by
 * another to produce one. ADR-0007.
 */

const STATE_LABEL: Record<ControlState, string> = {
  failing: 'Not met',
  passing: 'Met',
  not_evidenced: 'Not evidenced',
  not_applicable: 'Not applicable',
  not_assessed: 'Not assessed',
};

/** The verdict vocabulary the rest of the console uses, so nothing reads twice. */
const STATE_KEY: Record<ControlState, string> = {
  failing: 'not-met',
  passing: 'met',
  not_evidenced: 'not-evidenced',
  not_applicable: 'not-applicable',
  not_assessed: 'not-applicable',
};

interface Mark {
  readonly key: string;
  readonly glyph: string;
  readonly label: string;
}

/**
 * A glyph as well as a colour in every cell — the strip has to survive
 * greyscale printing of a screenshot and a colour-blind reader, and a row of
 * squares told apart only by tint survives neither.
 */
const MARK: Record<Verdict, Mark> = {
  PASS: { key: 'met', glyph: '✓', label: 'Met' },
  FAIL: { key: 'not-met', glyph: '✕', label: 'Not met' },
  INSUFFICIENT_DATA: { key: 'not-evidenced', glyph: '?', label: 'Not evidenced' },
  NOT_APPLICABLE: { key: 'not-applicable', glyph: '–', label: 'Not applicable' },
};

/** No finding at that assessment. An absence in what we looked at, never a verdict. */
const GAP: Mark = { key: 'gap', glyph: '·', label: 'No finding at this assessment' };

const BREAK_GLYPH = '≠';

const BREAK_MEANING =
  'The rule or the thresholds we apply to this control changed at this point. Assessments either ' +
  'side of a break are not measuring the same thing, so nothing is compared across one.';

const GAP_MEANING =
  'The control produced no finding at that assessment — it had not been added to the catalogue ' +
  'yet, or had been retired since. This is a gap in what we looked at, and not a verdict.';

/** The three figures that describe movement. Counts, never a ratio. */
const TALLY: ReadonlyArray<{
  key: 'remediatedInWindow' | 'newlyFailingInWindow' | 'withComparabilityBreak';
  label: string;
  rail: string;
  hint: string;
}> = [
  {
    key: 'remediatedInWindow',
    label: 'Put right in this period',
    rail: 'met',
    hint: 'seen not met and then met, under the same rule',
  },
  {
    key: 'newlyFailingInWindow',
    label: 'Seen to start failing',
    rail: 'not-met',
    hint: 'we saw the assessment before it, and it was not failing then',
  },
  {
    key: 'withComparabilityBreak',
    label: 'History broken by a change of ours',
    rail: 'not-evidenced',
    hint: 'our rule or thresholds moved, so the two sides are not comparable',
  },
];

/**
 * The bar under a tally figure is a count of units, capped — never a share of a
 * total. Same device, and same reason, as the drift view: a bar whose length is
 * count ÷ total is a compliance percentage wearing a different hat.
 */
const barLength = (count: number): CSSProperties => ({ '--n': count }) as CSSProperties;

interface StripProps {
  history: ControlHistory;
}

/**
 * One mark per assessment, oldest first, with breaks drawn between them.
 *
 * A gap is a cell, because an assessment happened there and we have nothing to
 * show for it. A break is not a cell at all — it sits between two of them, is
 * taller than the row, and carries no verdict glyph. Joining the two into one
 * vocabulary is the failure this whole view exists to avoid.
 */
const Strip = ({ history }: StripProps) => {
  const breakAfter = new Map(history.breaks.map((point) => [point.beforeScanId, point.reason]));

  return (
    <div className="pg-strip">
      {history.observations.map((observation) => {
        const mark = observation.verdict === null ? GAP : MARK[observation.verdict];
        const reason = breakAfter.get(observation.scanId);
        return (
          <span key={observation.scanId} className="pg-slot">
            <span
              className={`pg-cell pg-cell-${mark.key}`}
              title={`${asDate(observation.completedAt)} — ${mark.label}`}
            >
              <span aria-hidden="true">{mark.glyph}</span>
              <span className="sr-only">
                {asDate(observation.completedAt)}: {mark.label}
              </span>
            </span>
            {reason !== undefined && (
              <span className="pg-break" title={reason}>
                <span aria-hidden="true">{BREAK_GLYPH}</span>
                <span className="sr-only">Comparability break: {reason}</span>
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

const OpenFailure = ({ run }: { run: FailingRun }) => (
  <>
    <span className="pg-duration">Not met for {plural(run.days, 'day')}</span>
    <span className="note pg-since">since the assessment of {asDate(run.since)}</span>
    {run.qualification !== null && <p className="note pg-qualification">{run.qualification}</p>}
  </>
);

/**
 * `comparable === false` is the branch that matters. The control read not met
 * and then met, but our own rule moved in between, so there is no fix to
 * attribute to this client — and the server has nulled every field that would
 * carry a duration precisely so that nothing here can print one.
 */
const Fixed = ({ remediation }: { remediation: Remediation }) => {
  if (!remediation.comparable || remediation.daysFailing === null) {
    return (
      <>
        <span className="pg-duration pg-duration-unattributable">
          Changed to met on {asDate(remediation.fixedAt)}
        </span>
        <span className="note pg-since">This change cannot be attributed to work in the tenant</span>
        {remediation.qualification !== null && (
          <p className="note pg-qualification">{remediation.qualification}</p>
        )}
      </>
    );
  }

  return (
    <>
      <span className="pg-duration">
        Put right on {asDate(remediation.fixedAt)}, after {plural(remediation.daysFailing, 'day')}
      </span>
      {remediation.failingSince !== null && (
        <span className="note pg-since">not met from the assessment of {asDate(remediation.failingSince)}</span>
      )}
      {remediation.qualification !== null && (
        <p className="note pg-qualification">{remediation.qualification}</p>
      )}
    </>
  );
};

/**
 * The tab strip: every framework this client's reports cover, plus the whole
 * catalogue.
 *
 * **"All controls" is first and is not a framework.** Without it a control that
 * no framework in the list maps to would vanish from this screen entirely —
 * silently, and on the one tab that exists to show what has been assessed over
 * time. A control the catalogue evaluates and nothing displays is the worst
 * shape a compliance product can take, so the unfiltered view stays and the
 * framework tabs are added beside it.
 */
interface View {
  readonly id: string;
  readonly label: string;
  /** Null for "All controls". */
  readonly framework: Framework | null;
}

const ALL_CONTROLS = 'all';

/** The controls a framework's criteria name, deduplicated across them. */
const controlIdsOf = (framework: Framework): ReadonlySet<string> =>
  new Set(framework.criteria.flatMap((criterion) => criterion.controlIds));

interface ProgressProps {
  progress: ProgressReport | null;
  error: string | null;
  /**
   * The frameworks this client's reports are projected against: what their
   * account's package covers, narrowed by what has been chosen for them on the
   * Coverage tab. The same intersection the pipeline makes at render time, so
   * this screen and their report group by the same set — a tab here for a
   * framework their report does not contain would be a claim we do not print.
   */
  frameworks: readonly Framework[];
}

export const Progress = ({ progress, error, frameworks }: ProgressProps) => {
  /*
   * Before the early returns: hooks cannot be called conditionally, and both
   * the error and the loading state below return without rendering a strip.
   */
  const [view, setView] = useState<string>(ALL_CONTROLS);

  if (error !== null) {
    return (
      <div className="empty">
        <span className="empty-title">Progress could not be loaded</span>
        <p className="empty-body">{error}</p>
      </div>
    );
  }

  if (progress === null) {
    return <p className="note td-lead">Reading this client’s assessment history…</p>;
  }

  const first = progress.scans[0];
  const last = progress.scans[progress.scans.length - 1];

  const views: readonly View[] = [
    { id: ALL_CONTROLS, label: 'All controls', framework: null },
    ...frameworks.map((framework) => ({
      id: framework.id,
      label: framework.name,
      framework,
    })),
  ];
  /*
   * Falling back rather than trusting the stored id. Moving between clients
   * keeps this component mounted, and the framework tab that was open for one
   * client may not be covered by the next one's package.
   */
  const active = views.find((entry) => entry.id === view) ?? (views[0] as View);

  const shown =
    active.framework === null
      ? progress.controls
      : progress.controls.filter((history) =>
          controlIdsOf(active.framework as Framework).has(history.controlId),
        );

  return (
    <div className="pg">
      {views.length > 1 && (
        <nav className="tabs-underline pg-tabs" aria-label="Framework">
          {views.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className="tab"
              aria-selected={entry.id === active.id}
              role="tab"
              onClick={() => setView(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      )}

      {/*
        The framework's own words about what it is, carried from the catalogue.
        It is on the report for a reason — this screen shows the same material
        to the same people, and a framework's name above a table of verdicts
        invites exactly the reading the disclaimer exists to refuse.
      */}
      {active.framework !== null && (
        <p className="note td-lead">
          {active.framework.version}. {active.framework.disclaimer}
        </p>
      )}

      {first !== undefined && last !== undefined && (
        <p className="note td-lead">
          {plural(progress.scans.length, 'completed assessment')}, from{' '}
          <span className="num">{asDate(first.completedAt)}</span> to{' '}
          <span className="num">{asDate(last.completedAt)}</span>. Durations are measured to{' '}
          <span className="num">{asDate(progress.asOf)}</span>.
        </p>
      )}

      {progress.notes.length > 0 && (
        <div className="callout callout-caution">
          <span className="callout-title">Read this history with care</span>
          <ul className="dw-plain-list">
            {progress.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/*
        The three movement figures are the server's, counted over every control.
        They are shown on "All controls" and nowhere else: re-counting them per
        framework would be this file making a judgement, which is the one thing
        it does not do — and rendering the client-wide figures under a framework
        heading would be worse, because they would be read as that framework's.
      */}
      {active.framework !== null && !progress.insufficientHistory && (
        <p className="note td-lead">
          The movement figures are counted across every control this client has, and are on the
          All controls tab.
        </p>
      )}

      {active.framework === null && !progress.insufficientHistory && (
        <div className="stat-grid">
          {TALLY.map((entry) => {
            /*
             * Null is withheld, not zero and not missing. Where our own
             * catalogue or engine moved inside the window the movement counts
             * are null (ADR-0036), because the figure would read as a statement
             * about this client when the change may have been ours. Drawn as a
             * dash with the reason in its place, since an empty tile beside two
             * populated ones reads as a bug — and a bar of length null would
             * silently draw as a bar of length zero, which is a claim.
             */
            const value = progress.counts[entry.key];
            return (
              <div key={entry.key} className="stat stat-tile">
                <span className="stat-figure">{value === null ? '—' : value}</span>
                {value !== null && (
                  <span
                    className={`dw-bar dw-bar-${entry.rail}`}
                    style={barLength(value)}
                    aria-hidden="true"
                  />
                )}
                <span className="stat-label">{entry.label}</span>
                <span className="stat-hint">
                  {value === null ? 'not counted — see the note above' : entry.hint}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {shown.length === 0 ? (
        <div className="empty">
          <span className="empty-title">Nothing to show yet</span>
          <p className="empty-body">
            {active.framework === null
              ? 'No control produced a finding in this period, so there is no history to describe.'
              : `No control mapped to ${active.framework.name} produced a finding in this period. The controls it maps to may not have been assessed yet, or this client may have no assessment old enough to compare against.`}
          </p>
        </div>
      ) : (
        <>
          <div className="tbl-wrap">
            <table className="tbl pg-table">
              <caption className="sr-only">
                {active.framework === null
                  ? 'Each control, where it stands now, and its verdict at every assessment in this period'
                  : `Each control mapped to ${active.framework.name}, where it stands now, and its verdict at every assessment in this period`}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Control</th>
                  <th scope="col">Where it stands</th>
                  <th scope="col">Each assessment, oldest first</th>
                  <th scope="col">How long</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((history) => (
                  <tr key={history.controlId}>
                    <th scope="row">
                      <span className="label mono pg-id">{history.controlId}</span>
                      <span className="pg-title">{history.title}</span>
                    </th>
                    <td>
                      <span className={`verdict verdict-${STATE_KEY[history.state]}`}>
                        {STATE_LABEL[history.state]}
                      </span>
                    </td>
                    <td>
                      <Strip history={history} />
                    </td>
                    <td className="pg-age">
                      {history.remediation !== null && <Fixed remediation={history.remediation} />}
                      {history.openFailure !== null && <OpenFailure run={history.openFailure} />}
                      {history.remediation === null && history.openFailure === null && (
                        <span className="dw-void">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <details className="pg-legend">
            <summary>How to read the marks</summary>
            <dl className="pg-key">
              <dt>
                <span className="pg-cell pg-cell-met" aria-hidden="true">
                  {MARK.PASS.glyph}
                </span>
              </dt>
              <dd>Met at that assessment.</dd>
              <dt>
                <span className="pg-cell pg-cell-not-met" aria-hidden="true">
                  {MARK.FAIL.glyph}
                </span>
              </dt>
              <dd>Not met at that assessment.</dd>
              <dt>
                <span className="pg-cell pg-cell-not-evidenced" aria-hidden="true">
                  {MARK.INSUFFICIENT_DATA.glyph}
                </span>
              </dt>
              <dd>
                Not evidenced — the control could not be assessed. Neither a pass nor a failure, and
                never counted towards anything.
              </dd>
              <dt>
                <span className="pg-cell pg-cell-not-applicable" aria-hidden="true">
                  {MARK.NOT_APPLICABLE.glyph}
                </span>
              </dt>
              <dd>Not applicable — the thing the control governs did not exist in this tenant.</dd>
              <dt>
                <span className="pg-cell pg-cell-gap" aria-hidden="true">
                  {GAP.glyph}
                </span>
              </dt>
              <dd>A gap. {GAP_MEANING}</dd>
              <dt>
                <span className="pg-break" aria-hidden="true">
                  {BREAK_GLYPH}
                </span>
              </dt>
              <dd>A break. {BREAK_MEANING}</dd>
            </dl>
          </details>
        </>
      )}
    </div>
  );
};
